import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, Ticket } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { toast } from '@/components/ui/Toast'
import { CollaboratorsService, type BatchCouponHolder } from '@/services/modules/CollaboratorsService'

/**
 * /my-company/collaborators/aplicar-cupom
 *
 * Lista os titulares da empresa que têm plano mas ainda NÃO têm cupom, em ordem
 * alfabética, com a data de cadastro e um select com os cupons válidos para o
 * plano de cada um (a 1ª opção já vem selecionada). Confirmando, aplica os
 * cupons em lote. O backend revalida todas as regras do cupom por titular.
 */
export function MyCompanyBatchCouponPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [holders, setHolders] = useState<BatchCouponHolder[]>([])
  const [selections, setSelections] = useState<Record<string, string>>({})

  useEffect(() => {
    CollaboratorsService.couponBatchCandidates()
      .then((list) => {
        setHolders(list)
        const initial: Record<string, string> = {}
        for (const h of list) if (h.availableCoupons[0]) initial[h.id] = h.availableCoupons[0].id
        setSelections(initial)
      })
      .catch(() => toast.error('Erro ao carregar titulares.'))
      .finally(() => setLoading(false))
  }, [])

  const applicableCount = useMemo(() => Object.values(selections).filter(Boolean).length, [selections])

  async function apply() {
    const items = Object.entries(selections)
      .filter(([, couponId]) => !!couponId)
      .map(([collaborator_id, discount_coupon_id]) => ({ collaborator_id, discount_coupon_id }))
    if (items.length === 0) {
      toast.error({ title: 'Nada para aplicar', description: 'Selecione ao menos um cupom.' })
      return
    }
    setSaving(true)
    try {
      const res = await CollaboratorsService.batchApplyCoupon(items)
      toast.success({
        title: 'Cupons aplicados',
        description: `${res.applied} titular(es) atualizado(s)${res.skipped ? ` · ${res.skipped} ignorado(s)` : ''}.`,
      })
      navigate('/my-company/collaborators')
    } catch (e) {
      toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao aplicar cupons.')
    } finally { setSaving(false) }
  }

  return (
    <>
      <PageHeader
        title="Aplicar cupom em lote"
        subtitle="Titulares com plano e sem cupom — escolha o cupom de cada um"
        crumbs={[{ label: 'Minha empresa' }, { label: 'Colaboradores', to: '/my-company/collaborators' }, { label: 'Aplicar cupom em lote' }]}
        backTo="/my-company/collaborators"
        actions={
          <Button onClick={apply} loading={saving} disabled={loading || applicableCount === 0} iconLeft={<Ticket size={14} />}>
            Aplicar {applicableCount > 0 ? `(${applicableCount})` : ''}
          </Button>
        }
      />

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 size={28} className="animate-spin text-gray-400" /></div>
      ) : holders.length === 0 ? (
        <div className="bg-white rounded-md ring-1 ring-gray-200 p-10 text-center text-sm text-gray-500">
          Nenhum titular com plano e sem cupom para aplicar.
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-md ring-1 ring-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left   text-[11px] font-semibold uppercase tracking-wider text-gray-500">Colaborador</th>
                <th className="px-4 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-500 w-44">Data de cadastro</th>
                <th className="px-4 py-2 text-left   text-[11px] font-semibold uppercase tracking-wider text-gray-500 w-52">Plano</th>
                <th className="px-4 py-2 text-left   text-[11px] font-semibold uppercase tracking-wider text-gray-500 w-72">Cupom</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {holders.map((h) => (
                <tr key={h.id}>
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-800">{h.name}</div>
                    <div className="text-[11px] text-gray-400">#{h.code}</div>
                  </td>
                  <td className="px-4 py-2 text-center text-gray-600">{h.formatted?.createdAt ?? '—'}</td>
                  <td className="px-4 py-2 text-gray-700">{h.planName ?? '—'}</td>
                  <td className="px-4 py-2">
                    {h.availableCoupons.length === 0 ? (
                      <span className="text-xs text-gray-400">Sem cupom disponível</span>
                    ) : (
                      <Select
                        value={selections[h.id] ?? ''}
                        onChange={(e) => setSelections((prev) => ({ ...prev, [h.id]: e.target.value }))}
                        options={h.availableCoupons.map((c) => ({
                          value: c.id,
                          label: `${c.code}${c.formatted?.value ? ` — ${c.formatted.value}` : ''}`,
                        }))}
                        placeholder="Não aplicar"
                        searchPlaceholder="Buscar cupom"
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
